import {chain, chunk, isFunction} from 'lodash';
import {batch} from 'react-redux';
import {compose} from 'redux';
import uuid from 'uuid-random';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {getConversationFinder} from '../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {AppTask} from '../../../../srcCommon/tasks/appTask';
import {scheduleOfflineTaskAsyncAction} from '../../../../srcCommon/tasks/taskActions';
import {createCountersUpdaterForConversations} from '../../../helpers/counterHelpers';
import {ConversationUpdater} from '../../../helpers/models/conversationHelpers';
import {ApiConversationBatch} from '../../../models/api/conversationBatchModel';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {
  ConversationPatch,
  ConversationPatchBuilder,
  serializeConversationPatch,
} from '../../../models/api/conversationPatchModel';
import {mergeDataGroups} from '../../../models/api/dataGroupModel';
import {patchApiAsync} from '../../../services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {lockAllAsync} from '../../../services/locks/lock';
import {findConversationLock} from '../../../services/locks/resourceLocks';
import {Dispatch, StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {getHasOnlineClientCounters} from '../../featureFlags/featureFlagsSelectors';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../optimisticUpdates/optimisticUpdatesActions';
import {OptimisticUpdater} from '../../optimisticUpdates/optimisticUpdatesState';
import {showConversationUndoBannerAction} from '../../panels/app/banners/bannersActions';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext, makeDefaultCellsContext} from '../../panels/cells/context/cellsContext';
import {getOptionalFirstAppPanelId} from '../../panels/panelsSelectors';
import {updateClientCountersForConversationsAction} from '../counters/countersActions';
import {updateDataAction} from '../dataGroupsActions';
import {getConversations} from '../dataSelectors';
import {DataState} from '../dataState';
import {getUser} from '../teammates/teammatesSelectors';
import {MAX_NUMBER_OF_CONVERSATIONS_PER_REQUEST} from './conversationsHelpers';

/*
 * Types.
 */

interface ConversationsTaskUndoOptions {
  type: ConversationUndoBannerTypesEnum;
}

export interface GenericConversationsTaskOptions {
  cellsContext?: CellsContext;
  offlineTask?: AppTask;
  undo?: ConversationsTaskUndoOptions;
  apiMetaData?: unknown;
  dataUpdater?: OptimisticUpdater; // Use for non-conversation specific updates.
}

/*
 * Generic conversation update action.
 */

export function updateConversationsAction(
  conversationIds: ReadonlyArray<number>,
  patchOrPatchBuilder: ConversationPatch | ConversationPatchBuilder,
  transform: ConversationUpdater,
  options: GenericConversationsTaskOptions = {},
): ThunkAsyncAction {
  if (conversationIds.length > MAX_NUMBER_OF_CONVERSATIONS_PER_REQUEST) {
    return batchUpdateConversationsAction(conversationIds, patchOrPatchBuilder, transform, options);
  }

  // If provided, use the offline task ID for optimistic update tracking.
  const actionId = options.offlineTask ? options.offlineTask.id : uuid();

  // Set up optimistic updates independent from the current state.
  const makeOptimisticUpdaterForConversations = createOptimisticUpdaterForConversationsMaker(transform);

  return async (dispatch, getState) => {
    const appState = getState();

    const hasOnlineClientCounters = getHasOnlineClientCounters(appState);

    // Compute the final optimistic updater
    const optimisticUpdaterForConversationAndCounters = hasOnlineClientCounters
      ? createCountersUpdaterForConversations(
          conversationIds,
          makeOptimisticUpdaterForConversations(conversationIds),
        )
      : makeOptimisticUpdaterForConversations(conversationIds);

    const optimisticUpdater = options.dataUpdater
      ? compose(options.dataUpdater, optimisticUpdaterForConversationAndCounters)
      : optimisticUpdaterForConversationAndCounters;

    batch(() => {
      // Show the undo banner.
      maybeShowUndoUpdateConversationsBanner(dispatch, getState, conversationIds, transform, options);

      // Perform the API request with optimistic updates.
      dispatch(startOptimisticUpdateAction(actionId, optimisticUpdater));
    });

    try {
      // Acquire locks on the affected conversations, since the patches are dependent on conversation state.
      const conversationLocks = chain(conversationIds)
        .sortBy()
        .map((id) => findConversationLock(id))
        .value();
      await lockAllAsync(conversationLocks, async () => {
        const result = await performUpdateConversationsAsync(
          getState,
          conversationIds,
          patchOrPatchBuilder,
          options,
        );

        // Turn the result into a plain action.
        const mergedDataGroup = mergeDataGroups(...result.dataGroups);
        const resultAction = updateDataAction(mergedDataGroup);

        // Update client-side counters until we get a counters pusher update.
        if (hasOnlineClientCounters) {
          dispatch(
            updateClientCountersForConversationsAction(conversationIds, () => {
              dispatch(finishOptimisticUpdateAction(actionId, resultAction));
            }),
          );
          return;
        }

        // Finish the optimistic update.
        dispatch(finishOptimisticUpdateAction(actionId, resultAction));
      });
    } catch (error) {
      // Spawn an offline task, if appropriate.
      const {offlineTask} = options;
      const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
      if (offlineTask && isOfflineError) {
        // This will replace the optimistic update.
        await dispatch(scheduleOfflineTaskAsyncAction(offlineTask, error));
        return;
      }

      // Remove the optimistic update.
      dispatch(finishOptimisticUpdateAction(actionId));
      throw error;
    }
  };
}

// To avoid API request timeouts, split up into chunks and drop any offline tasks.
function batchUpdateConversationsAction(
  conversationIds: ReadonlyArray<number>,
  patchOrPatchBuilder: ConversationPatch | ConversationPatchBuilder,
  transform: ConversationUpdater,
  options: GenericConversationsTaskOptions = {},
): ThunkAsyncAction {
  return async (dispatch) => {
    const batches = chunk(conversationIds, MAX_NUMBER_OF_CONVERSATIONS_PER_REQUEST);
    await Promise.all(
      batches.map((batchConversationIds) =>
        dispatch(
          updateConversationsAction(batchConversationIds, patchOrPatchBuilder, transform, {
            ...options,
            offlineTask: undefined,
          }),
        ),
      ),
    );
  };
}

function createOptimisticUpdaterForConversationsMaker(transform: ConversationUpdater) {
  return (conversations: readonly number[]): OptimisticUpdater =>
    createObjectPropertyUpdater<DataState, 'conversations'>(
      'conversations',
      createObjectPropertyUpdater(
        'byId',
        compose(...conversations.map((id) => createResourceMapValueUpdater(id, createUpdater(transform)))),
      ),
    );
}

function maybeShowUndoUpdateConversationsBanner(
  dispatch: Dispatch,
  getState: StateGetter,
  conversationIds: ReadonlyArray<number>,
  transform: ConversationUpdater,
  options: GenericConversationsTaskOptions,
) {
  // Use the UI state (with optimistic updates) to set up the undo banner.
  const state = getState();
  const user = getUser(state);

  const conversationFinder = getConversationFinder(state);

  const oldConversations = conversationIds.map(conversationFinder).filter(isDefined);
  const newConversations = oldConversations.map(transform);
  const onlyOldConversation =
    conversationIds.length === 1 ? getConversations(state)[conversationIds[0]] : undefined;

  maybeShowUndoBanner(
    dispatch,
    getState,
    options.cellsContext || makeDefaultCellsContext(user, false),
    options.undo,
    oldConversations,
    newConversations,
    onlyOldConversation,
  );
}

async function performUpdateConversationsAsync(
  getState: StateGetter,
  conversationIds: ReadonlyArray<number>,
  patchOrPatchBuilder: ConversationPatch | ConversationPatchBuilder,
  options: GenericConversationsTaskOptions,
) {
  const conversations = isFunction(patchOrPatchBuilder)
    ? conversationIds
        .map((id) => {
          const patch = patchOrPatchBuilder(id);
          return patch && serializeConversationPatch(id, patch);
        })
        .filter(isDefined)
    : conversationIds.map((id) => serializeConversationPatch(id, patchOrPatchBuilder));

  const state = getState();
  // Perform the request.
  const {company} = state.originalData;
  const url = `${company.url}/conversations`;
  const extraBody = options.apiMetaData ? {_meta: options.apiMetaData} : {};
  const body = {conversations, ...extraBody};
  return patchApiAsync(ApiConversationBatch, url, body);
}

function maybeShowUndoBanner(
  dispatch: Dispatch,
  getState: StateGetter,
  cellsContext: CellsContext,
  undoOptions: ConversationsTaskUndoOptions | undefined,
  oldConversations: ReadonlyArray<ConversationBaseModel>,
  newConversations: ReadonlyArray<ConversationBaseModel>,
  onlyOldConversation: ConversationModel | undefined,
) {
  // If no undo was requested or if we should prevent, nothing to do.
  if (!undoOptions) {
    return;
  }

  // Find the panel to display the undo action in.
  // TODO: Find a better way to get a panel ID in here.
  const appPanelId = getOptionalFirstAppPanelId(getState());

  // We may not have an app panel in tests.
  // TODO: Refactor action creators to separate data concerns from UI/panel concerns.
  if (!appPanelId) {
    return;
  }

  // Dispatch the undo banner.
  dispatch(
    showConversationUndoBannerAction(
      appPanelId,
      cellsContext,
      undoOptions.type,
      oldConversations,
      newConversations,
      onlyOldConversation,
    ),
  );
}
